import { Check } from 'lucide-react'

import type { CatalogPlan } from '~/features/billing/types'

import { formatCompact, formatCount, formatGb, formatUsd } from '~/features/billing/utils'

type Benefit = { title: string; description?: string }

const custom = (value: number | null | undefined, format: (value: number) => string) =>
  value == null ? 'Custom' : format(value)

function planBenefits(plan: CatalogPlan): Benefit[] {
  const { allowances, limits, overage, addons } = plan
  const hardStop = plan.id === 'free'
  const items: Benefit[] = []

  items.push({
    title: `${custom(allowances?.storageGb, formatGb)} storage`,
    description:
      overage ? `then ${formatUsd(overage.storagePerGb)} per GB-month`
      : hardStop ? 'Writes pause when full; reads keep working'
      : undefined
  })
  items.push({
    title: `${custom(allowances?.smartSearches, formatCount)} Smart Searches / month`,
    description: overage ? `then ${formatUsd(overage.smartSearchPer1k)} per 1K` : undefined
  })
  items.push({
    title: `${custom(allowances?.embeddingTokens, formatCompact)} embedding tokens / month`,
    description: overage ? `then ${formatUsd(overage.embeddingsPer1M)} per 1M` : undefined
  })

  if (limits) {
    items.push({
      title: limits.projects == null ? 'Unlimited projects' : `${formatCount(limits.projects)} projects`,
      description: addons ? `then ${formatUsd(addons.extraProject.month)}/month each` : undefined
    })
    items.push({
      title:
        limits.seats == null ?
          'Unlimited seats'
        : `${formatCount(limits.seats)} seat${limits.seats === 1 ? '' : 's'}`,
      description: addons ? `then ${formatUsd(addons.extraSeat.month)}/month each` : undefined
    })
  }

  if (plan.byok) items.push({ title: 'Bring your own model key', description: 'coming soon' })

  for (const feature of plan.features ?? []) {
    items.push({ title: feature })
  }

  return items
}

export function PlanBenefits({ plan }: { plan: CatalogPlan }) {
  return (
    <ul className="flex flex-col gap-1">
      {planBenefits(plan).map((benefit) => (
        <li className="mb-2 flex items-start gap-2" key={benefit.title}>
          <Check className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p>{benefit.title}</p>
            {benefit.description ?
              <p className="text-content3">{benefit.description}</p>
            : null}
          </div>
        </li>
      ))}
    </ul>
  )
}
